import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getTestId, type TestIdProps } from '../utils';

/**
 * Uppercase section heading used to label groups of fields or settings blocks.
 *
 * Renders as a `<p>` with muted, uppercase, letter-spaced styling.
 * Use the `size` prop to match the surrounding typography scale.
 *
 * @example
 * <SectionLabel>Account Details</SectionLabel>
 * <SectionLabel size="xs" className="mb-xs">Payment Info</SectionLabel>
 * <SectionLabel testId="section-label-payment">Payment Info</SectionLabel>
 */

type BaseSectionLabelAttributes = Pick<
  React.HTMLAttributes<HTMLParagraphElement>,
  'className' | 'children' | 'id'
>;

const sectionLabelVariants = cva('font-semibold text-foreground-muted uppercase tracking-wide', {
  variants: {
    size: {
      xs: 'text-2xs',
      sm: 'text-sm'
    }
  },
  defaultVariants: {
    size: 'sm'
  }
});

type SectionLabelVariants = VariantProps<typeof sectionLabelVariants>;

export interface SectionLabelProps
  extends BaseSectionLabelAttributes, SectionLabelVariants, TestIdProps {}

export const SectionLabel = React.forwardRef<HTMLParagraphElement, SectionLabelProps>(
  ({ className, size, testId, id, ...props }, ref) => (
    <p
      ref={ref}
      id={id}
      data-testid={getTestId({ testId, id })}
      className={cn(sectionLabelVariants({ size, className }))}
      {...props}
    />
  )
);

SectionLabel.displayName = 'SectionLabel';
